import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

// Recap the REAL 9-step pipeline (18 stages grouped) — no invented latencies.
const STEPS = [
  { n: 1, label: "Query · Intent · Acronyms", color: COLORS.accentBlue },
  { n: 2, label: "Rewrite · Decompose · Cache", color: COLORS.accentPurple },
  { n: 3, label: "Hybrid Retrieval + Title", color: COLORS.accentTeal },
  { n: 4, label: "Artifact-Tier Quarantine", color: COLORS.accentRed },
  { n: 5, label: "Knowledge-Graph Augment", color: COLORS.accentGreen },
  { n: 6, label: "CRAG Quality Gate", color: COLORS.accentYellow },
  { n: 7, label: "Rerank · Diversify · Gate", color: COLORS.accentPink },
  { n: 8, label: "Synthesis · Grounding", color: COLORS.accentOrange },
  { n: 9, label: "Completeness · Prune", color: COLORS.accentGreen },
];

export const Scene8Outro: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const exitOpacity = interpolate(frame, [130, 150], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.08} />

      {/* Title */}
      {(() => {
        const tAppear = spring({ frame, fps, config: SPRING.snappy });
        return (
          <div
            style={{
              position: "absolute",
              top: 90,
              left: "50%",
              transform: "translate(-50%, 0)",
              textAlign: "center",
              opacity: interpolate(tAppear, [0, 1], [0, 1]),
            }}
          >
            <div style={{ fontSize: 44, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
              The Full Pipeline:{" "}
              <span
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accentBlue}, ${COLORS.accentPurple})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                18 real stages
              </span>
            </div>
            <div style={{ fontSize: 18, fontFamily: FONTS.body, color: COLORS.textSecondary, marginTop: 8 }}>
              user query → grounded, cited answer — nothing invented
            </div>
          </div>
        );
      })()}

      {/* 9-step recap chips */}
      <div
        style={{
          position: "absolute",
          top: 250,
          left: "50%",
          transform: "translate(-50%, 0)",
          width: 1500,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 16,
        }}
      >
        {STEPS.map((s, i) => {
          const a = spring({ frame: frame - 18 - i * 7, fps, config: SPRING.snappy });
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 22px",
                borderRadius: 12,
                background: `${s.color}0D`,
                border: `1px solid ${s.color}40`,
                opacity: interpolate(a, [0, 1], [0, 1]),
                transform: `translateY(${interpolate(a, [0, 1], [16, 0])}px)`,
              }}
            >
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: `${s.color}20`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: FONTS.mono,
                  fontSize: 14,
                  fontWeight: 700,
                  color: s.color,
                }}
              >
                {s.n}
              </span>
              <span style={{ fontFamily: FONTS.mono, fontSize: 16, color: COLORS.textPrimary }}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Key takeaways */}
      <div
        style={{
          position: "absolute",
          bottom: 130,
          left: "50%",
          transform: "translate(-50%, 0)",
          display: "flex",
          gap: 50,
        }}
      >
        {[
          { label: "vector + BM25 + title", value: "Hybrid Retrieval", icon: "🔎", delay: 80 },
          { label: "test/eval/screenshot dropped", value: "Artifact Quarantine", icon: "🧹", delay: 88 },
          { label: "corrective only when weak", value: "CRAG Gate", icon: "🧪", delay: 96 },
          { label: "splice facts · prune sources", value: "Grounded & Complete", icon: "🛡️", delay: 104 },
        ].map((item, i) => {
          const iAppear = spring({ frame: frame - item.delay, fps, config: SPRING.snappy });
          return (
            <div
              key={i}
              style={{
                textAlign: "center",
                width: 240,
                opacity: interpolate(iAppear, [0, 1], [0, 1]),
                transform: `translateY(${interpolate(iAppear, [0, 1], [15, 0])}px)`,
              }}
            >
              <div style={{ fontSize: 30, marginBottom: 8 }}>{item.icon}</div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>
                {item.value}
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.textMuted, marginTop: 4 }}>
                {item.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Next episode tease */}
      {(() => {
        const nextAppear = spring({ frame: frame - 100, fps, config: SPRING.gentle });
        return (
          <div
            style={{
              position: "absolute",
              bottom: 46,
              left: "50%",
              transform: "translate(-50%, 0)",
              opacity: interpolate(nextAppear, [0, 1], [0, 0.6]),
              fontFamily: FONTS.mono,
              fontSize: 14,
              color: COLORS.textMuted,
            }}
          >
            Next Episode: Document Ingestion Deep Dive →
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
